import React, { useRef, useEffect, createContext, useContext } from 'react';
import {
  Box,
  Portal,
  useColorModeValue,
  useOutsideClick,
  IconButton,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { Icon, palette } from '@m12s/component-library';
import { falTimes } from 'lib/icons';

// Create a context for drawer
const PersistentDrawerContext = createContext({
  styles: {},
  onClose: () => {},
});

const useDrawerContext = () => {
  return useContext(PersistentDrawerContext);
};

export const PersistentDrawerCloseButton = (props) => {
  const { styles, onClose } = useDrawerContext();

  return (
    <IconButton
      onClick={onClose}
      aria-label="Close"
      icon={
        <Icon
          size="2rem"
          color={palette.Grey700}
          icon={falTimes}
          cursor="pointer"
        />
      }
      variant="ghost"
      {...props}
      __css={{
        position: 'absolute',
        top: '50%',
        right: '1.5rem',
        transform: 'translateY(-50%)',
        zIndex: 1,
        background: 'transparent',
        border: 0,
        padding: 0,
        margin: 0,
        minWidth: 'auto',
        height: 'auto',
        ...styles.closeButton,
      }}
    />
  );
};

export const PersistentDrawerOverlay = ({ isOpen, onClick, ...rest }) => {
  const bg = useColorModeValue('rgba(0, 0, 0, 0.4)', 'rgba(0, 0, 0, 0.6)');
  const { styles } = useDrawerContext();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: isOpen ? 1 : 0 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: bg,
        zIndex: 1400,
        pointerEvents: isOpen ? 'auto' : 'none',
        ...styles.overlay,
      }}
      onClick={onClick}
      {...rest}
    />
  );
};

export const PersistentDrawerContent = ({
  isOpen,
  placement = 'right',
  children,
  sx = {},
  ...rest
}) => {
  const { styles } = useDrawerContext();

  // Define placement-specific styles
  const placementStyles = {
    top: {
      cssProps: {
        top: 0,
        left: 0,
        right: 0,
      },
      motionProps: {
        initial: { y: '-100%' },
        animate: { y: isOpen ? 0 : '-100%' },
      },
    },
    bottom: {
      cssProps: {
        bottom: 0,
        left: 0,
        right: 0,
      },
      motionProps: {
        initial: { y: '100%' },
        animate: { y: isOpen ? 0 : '100%' },
      },
    },
    left: {
      cssProps: {
        top: 0,
        bottom: 0,
        left: 0,
      },
      motionProps: {
        initial: { x: '-100%' },
        animate: { x: isOpen ? 0 : '-100%' },
      },
    },
    right: {
      cssProps: {
        top: 0,
        bottom: 0,
        right: 0,
      },
      motionProps: {
        initial: { x: '100%' },
        animate: { x: isOpen ? 0 : '100%' },
      },
    },
  };

  const placementStyle = placementStyles[placement] || placementStyles.right;

  return (
    <motion.div
      initial={placementStyle.motionProps.initial}
      animate={placementStyle.motionProps.animate}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      style={{
        position: 'fixed',
        background: 'white',
        boxShadow: '0 0 10px rgba(0,0,0,0.2)',
        zIndex: 1500,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        ...placementStyle.cssProps,
        ...styles.content,
        ...sx,
      }}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

export const PersistentDrawerHeader = (props) => {
  const { styles } = useDrawerContext();
  return (
    <Box
      color={palette.Grey700}
      bg={palette.White}
      borderBottom={`1px solid ${palette.Grey400}`}
      {...props}
      __css={{
        px: 6,
        py: 4,
        fontSize: 'xl',
        fontWeight: '500',
        flexShrink: 0,
        position: 'relative',
        ...styles.header,
      }}
    />
  );
};

export const PersistentDrawerBody = (props) => {
  const { styles } = useDrawerContext();
  return (
    <Box
      {...props}
      __css={{
        px: 6,
        py: 2,
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        height: '100%',
        ...styles.body,
      }}
    />
  );
};

export const PersistentDrawerFooter = (props) => {
  const { styles } = useDrawerContext();
  return (
    <Box
      {...props}
      __css={{
        px: 6,
        py: 4,
        display: 'flex',
        justifyContent: 'flex-end',
        flexShrink: 0,
        ...styles.footer,
      }}
    />
  );
};

const PersistentDrawer = ({
  isOpen,
  onClose,
  children,
  placement = 'bottom',
  size = 'md',
  closeOnOverlayClick = true,
  closeOnEsc = true,
  sx = {},
  ...rest
}) => {
  const drawerRef = useRef(null);

  // Handle outside clicks
  useOutsideClick({
    ref: drawerRef,
    handler: () => {
      if (isOpen && closeOnOverlayClick) {
        onClose();
      }
    },
  });

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen && closeOnEsc) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, closeOnEsc]);

  // Define sizes
  const sizes = {
    xs: { width: 'xs', maxWidth: '15rem' },
    sm: { width: 'sm', maxWidth: '20rem' },
    md: { width: 'md', maxWidth: '28rem' },
    lg: { width: 'lg', maxWidth: '32rem' },
    xl: { width: 'xl', maxWidth: '36rem' },
    full: { width: 'full', maxWidth: '100%' },
  };

  const sizeValue = sizes[size] || sizes.md;

  // Apply size based on placement
  const sizeStyles = {
    left: { width: sizeValue.maxWidth, height: '100vh' },
    right: { width: sizeValue.maxWidth, height: '100vh' },
    top: { height: sizeValue.maxWidth, width: '100vw' },
    bottom: { height: sizeValue.maxWidth, width: '100vw' },
  };

  const finalSizeStyles = sizeStyles[placement] || sizeStyles.right;

  // Define border radius styles based on placement
  const borderRadiusStyles = {
    top: {
      borderBottomLeftRadius: '0.375rem',
      borderBottomRightRadius: '0.375rem',
    },
    bottom: {
      borderTopLeftRadius: '0.375rem',
      borderTopRightRadius: '0.375rem',
    },
    left: {
      borderTopRightRadius: '0.375rem',
      borderBottomRightRadius: '0.375rem',
    },
    right: {
      borderTopLeftRadius: '0.375rem',
      borderBottomLeftRadius: '0.375rem',
    },
  };

  const placementBorderRadius = borderRadiusStyles[placement] || {};

  // Create default height for bottom and top placements
  const defaultHeight =
    placement === 'bottom' || placement === 'top'
      ? 'calc(100vh - 30px)'
      : '100vh';

  const defaultWidth =
    placement === 'left' || placement === 'right'
      ? 'calc(100vw - 30px)'
      : '100vw';

  // Create styles context
  const styles = {
    overlay: {},
    content: {
      ...finalSizeStyles,
      bg: useColorModeValue('white', 'gray.800'),
      color: useColorModeValue('gray.800', 'white'),
      height: defaultHeight,
      width: defaultWidth,
      ...placementBorderRadius,
      ...sx,
    },
    closeButton: {},
    header: {
      bg: useColorModeValue('white', 'gray.800'),
      color: useColorModeValue('gray.800', 'white'),
    },
    body: {},
    footer: {
      bg: useColorModeValue('white', 'gray.800'),
      color: useColorModeValue('gray.800', 'white'),
    },
  };

  // Create context value
  const contextValue = {
    styles,
    onClose,
  };

  return (
    <PersistentDrawerContext.Provider value={contextValue}>
      <Portal>
        <PersistentDrawerOverlay isOpen={isOpen} onClick={onClose} />
        <Box ref={drawerRef} {...rest}>
          <PersistentDrawerContent
            isOpen={isOpen}
            placement={placement}
            sx={sx}
          >
            {children}
          </PersistentDrawerContent>
        </Box>
      </Portal>
    </PersistentDrawerContext.Provider>
  );
};

export default PersistentDrawer;
